import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { DeviceFactory } from '../../../../device-factory/device-factory';
import { SitesService } from 'src/app/sites/sites.service';
import * as lo from 'lodash-es';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonService } from 'src/app/shared/services/common.service';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

@Component({
	selector: 'app-relative-truck-utilization',
	templateUrl: './relative-truck-utilization.component.html',
	styleUrls: ['./relative-truck-utilization.component.css']
})
export class RelativeTruckUtilizationComponent {
	@Input() devices: any = {};
	@Input() data: any = {};
	siteSubscription: any;

	siteId: number = 0;
	showChart = false;
	siteInfo: any = {};

	periods = [
		{ value: 'last_week', label: this.translateService.instant('durations.last_week') },
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'custom', label: this.translateService.instant('env.custom') },
	];

	chartTypes = [
		{ value: 'total_period', label: this.translateService.instant('g.total_period') },
		{ value: 'weekly', label: this.translateService.instant('alert_frequency.weekly') },
		{ value: 'monthly', label: this.translateService.instant('g.monthly') }
	];

	chartCriteria = [
		{ value: 'trucks_with_max_usage', label: this.translateService.instant('utilization.trucks_with_max_usage') },
		{ value: 'daily_available_ah', label: this.translateService.instant('utilization.daily_available_ah') },
	];

	truckTypes = [];
	selectedTruckType = "all";
	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown; 
	selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
	truckManufacturer = [];
	selectedtruckManufacturer = "all";
	selectedChartCriteria = this.chartCriteria[0].value;
	selectedPeriod = this.periods[0].value;
	selectedChartType = this.chartTypes[0].value;

	chart = {
		title: this.translateService.instant('utilization.relative_truck_utilization'),
		data: [],
		columnNames: [this.translateService.instant('g.device'), this.translateService.instant('g.usage_percentage')],
		config: {
			hAxis: {
				title: this.translateService.instant('g.device'),
			},
			vAxis: {
				title: this.translateService.instant('g.usage_percentage'),
			},
			colors: ['#3366CC', '#DC3912', '#FF9900', '#109618', '#990099', '#3B3EAC', '#0099C6', '#DD4477','#66AA00', '#B82E2E'],
			legend: {
				position: 'top',
				alignment: 'center',
				textStyle: { fontSize: 12 }
			},
			titleTextStyle: {
				fontSize: 16,
				bold: true,
				color: '#333',
				paddingBottom: 20,
				paddingTop: 20
			},
			isStacked: true,
		}
	}

	toDate = new Date();
	fromDate = new Date(this.toDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	siteInstallationDate: Date = new Date(0);

	dailyUtilizationData: any = {};
	devicesCapacities: any = {};

	currentPage: number = 0;
	pageSize: number = 10;
	devicesCount: number = 0;

	constructor(
		private translateService: TranslateService,
		private sideMenuService: SideMenuService,
		private route: ActivatedRoute,
		private sitesService: SitesService,
		private commonService: CommonService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;
		this.devicesCount = Object.keys(this.devices).length;

		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.siteInfo = siteInfo;
				this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();

				if (this.siteInstallationDate > this.fromDate)
					this.fromDate = this.siteInstallationDate;
			}

			this.prepareTruckTypes();
			this.getRelativeTruckUtilization();
		});
	}

	getRelativeTruckUtilization() {
		if (this.isDailyBased())
			return this.getDailyRelativeTruckUtilization();

		this.showChart = true;
		this.preparePeriodicUtilizationData();
	}

	private isDailyBased() {
		return this.selectedPeriod == 'custom' || this.selectedChartType != 'total_period';
	}

	private getSelectedDateRange() {
		if (this.selectedPeriod == 'custom')
			return { from: this.fromDate, to: this.toDate };

		return this.commonService.getDateRange(this.selectedPeriod);
	}

	getDailyRelativeTruckUtilization() {
		const range = this.getSelectedDateRange();
		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(range.from).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(range.to).getTime() + (zoneDiff * 60 * 1000));

		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		this.sitesService.getAhDailyUtilizationData(this.siteId, fromDate, toDate, [DeviceFactory.types.iotah]).subscribe((res: any) => {
			this.devicesCapacities = res?.capacities || {};
			this.dailyUtilizationData = res.data || {};

			this.prepareDailyUtilizationData();
		});
	}

	prepareUtilizationData() {
		if (this.isDailyBased())
			return this.prepareDailyUtilizationData();

		this.preparePeriodicUtilizationData();
	}

	preparePeriodicUtilizationData() {
		const periodData = this.data[this.selectedPeriod] || {};
		const devicesUsage = periodData?.allDays?.relativeUtilization || [];
		const filteredDevices = this.getFilteredDevices();
		const usageDataObject = lo.keyBy(devicesUsage, 'macAddress');

		this.chart.data = [];
		this.chart.columnNames = [this.translateService.instant('g.device'), this.translateService.instant('g.usage_percentage')];
		let maxUsage = 0;

		for (const deviceData of devicesUsage) {
			if (!deviceData || !deviceData.macAddress || !filteredDevices[deviceData.macAddress])
				continue;

			const ahUsage = deviceData.inuseAh || 0;
			if (ahUsage > maxUsage)
				maxUsage = ahUsage;
		}

		for (const macAddress in filteredDevices) {
			const device = filteredDevices[macAddress] || {};
			const deviceUsage = usageDataObject[macAddress] || {};
			const ahUsage = deviceUsage.inuseAh || 0;
			const capacity = (deviceUsage.battery_capacity || 0) * (deviceUsage.total_days_in_period || 0) * 0.8;
			const maxValue = this.selectedChartCriteria === 'trucks_with_max_usage' ? maxUsage : capacity;
			const usagePercentage = maxValue > 0 ? (ahUsage / maxValue) * 100 : 0;

			this.chart.data.push([device.truck_sn || device.serial_number, this.getUsagePercentage(usagePercentage)]);
		}

		if (!this.chart.data.length) {
			this.showChart = false;
			return;
		}

		this.showChart = true;
		this.chart.data.sort((a, b) => b[1] - a[1]);
		this.updateVAxisMax(this.chart.data)
		this.applyPagination();
	}

	prepareDailyUtilizationData() {
		this.chart.data = [];
		const preparedChartData = [];
		const filteredDevices = this.getFilteredDevices();
		const daysCount = Object.keys(this.dailyUtilizationData).length;
		this.chart.columnNames = [this.translateService.instant('g.device'), this.translateService.instant('g.usage_percentage')];

		const devicesUsage = this.aggregateDeviceUsage();
		const maxUsage = Math.max(0, ...Object.values(devicesUsage));

		if (!maxUsage) {
			this.showChart = false;
			this.chart.data = [];
			return;
		}

		switch (this.selectedChartType) {
			case 'total_period':
				this.prepareTotalPeriodChartData(filteredDevices, devicesUsage, maxUsage, daysCount, preparedChartData);
				break;

			case 'monthly':
				this.prepareMonthlyChartData(filteredDevices, maxUsage, daysCount, preparedChartData);
				break;

			case 'weekly':
				this.prepareWeeklyChartData(filteredDevices, maxUsage, daysCount, preparedChartData);
				break;
		}

		if (!preparedChartData.length) {
			this.showChart = false;
			this.chart.data = [];
			return;
		}

		this.showChart = true;
		this.chart.data = this.sortChartData(preparedChartData);
		this.updateVAxisMax(this.chart.data)
		this.applyPagination();
	}

	private aggregateDeviceUsage(): Record<string, number> {
		const usage: Record<string, number> = {};

		for (const day in this.dailyUtilizationData) {
			for (const mac in this.dailyUtilizationData[day]) {
				usage[mac] = (usage[mac] || 0) + ((this.dailyUtilizationData[day][mac] || 0) / 3600);
			}
		}

		return usage;
	}

	private prepareTotalPeriodChartData(
		filteredDevices: Record<string, any>,
		devicesUsage: Record<string, number>,
		maxUsage: number,
		daysCount: number,
		chartData: any[]
	) {
		for (const mac in filteredDevices) {
			const device = this.devices[mac] || {};
			const capacity = (this.devicesCapacities[mac] ?? 0) * daysCount * 0.8;
			const maxValue = this.selectedChartCriteria === 'trucks_with_max_usage' ? maxUsage : capacity;
			const usagePercentage = maxValue > 0 ? (devicesUsage[mac] || 0) / maxValue * 100 : 0;

			chartData.push([device.truck_sn || device.serial_number, this.getUsagePercentage(usagePercentage)]);
		}
	}

	private prepareMonthlyChartData(
		filteredDevices: Record<string, any>,
		maxUsage: number,
		daysCount: number,
		chartData: any[]
	) {
		const monthlyUsage: Record<string, Record<string, number>> = {};
		const months = new Set<string>();

		for (const day in this.dailyUtilizationData) {
			const month = moment.unix(+day).utc().format('YYYY-MM');
			months.add(month);

			for (const mac in this.dailyUtilizationData[day]) {
				monthlyUsage[mac] ||= {};
				monthlyUsage[mac][month] = (monthlyUsage[mac][month] || 0) + this.dailyUtilizationData[day][mac];
			}
		}

		this.chart.columnNames = [this.translateService.instant('g.device'), ...Array.from(months)];

		for (const mac in filteredDevices) {
			const device = this.devices[mac] || {};
			const row = [device.truck_sn || device.serial_number];

			Array.from(months).forEach(month => {
				const usage = (monthlyUsage[mac]?.[month] || 0) / 3600;
				const capacity = (this.devicesCapacities[mac] ?? 0) * (daysCount || 0) * 0.8;
				const maxValue = this.selectedChartCriteria === 'trucks_with_max_usage' ? maxUsage : capacity;
				const usagePercentage = maxValue > 0 ? (usage / maxValue) * 100 : 0;
				row.push(this.getUsagePercentage(usagePercentage));
			});

			chartData.push(row);
		}
	}

	private prepareWeeklyChartData(
		filteredDevices: Record<string, any>,
		maxUsage: number,
		daysCount: number,
		chartData: any[]
	) {
		const weeklyUsage: Record<string, Record<string, number>> = {};
		const weeks = new Set<string>();

		for (const day in this.dailyUtilizationData) {
			const date = moment.unix(+day).utc();
			const startOfWeek = date.clone().startOf('isoWeek');
			const endOfWeek = date.clone().endOf('isoWeek');
			const week = `${startOfWeek.format('YYYY-MM-DD')} to ${endOfWeek.format('YYYY-MM-DD')}`;
			weeks.add(week);

			for (const mac in this.dailyUtilizationData[day]) {
				weeklyUsage[mac] ||= {};
				weeklyUsage[mac][week] = (weeklyUsage[mac][week] || 0) + this.dailyUtilizationData[day][mac];
			}
		}

		const sortedWeeks = Array.from(weeks).sort();
		this.chart.columnNames = [this.translateService.instant('g.device'), ...sortedWeeks];

		for (const mac in filteredDevices) {
			const device = this.devices[mac] || {};
			const row = [device.truck_sn || device.serial_number];

			sortedWeeks.forEach(week => {
				const usage = (weeklyUsage[mac]?.[week] || 0) / 3600;
				const capacity = (this.devicesCapacities[mac] ?? 0) * (daysCount || 0) * 0.8;
				const maxValue = this.selectedChartCriteria === 'trucks_with_max_usage' ? maxUsage : capacity;
				const usagePercentage = maxValue > 0 ? (usage / maxValue) * 100 : 0;
				row.push(this.getUsagePercentage(usagePercentage));
			});

			chartData.push(row);
		}
	}

	private sortChartData(data: any[]): any[] {
		return data.sort((a, b) => {
			const sumA = a.slice(1).reduce((sum, val) => sum + val, 0);
			const sumB = b.slice(1).reduce((sum, val) => sum + val, 0);
			return sumB - sumA;
		});
	}

	private getUsagePercentage(usagePercentage: number): number {
		return Math.min(100, Math.round(usagePercentage || 0));
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'period':
				this.selectedPeriod = event.value;
				this.currentPage = 0;
				return this.getRelativeTruckUtilization();

			case 'truckType':
				this.selectedTruckType = event.value;
				break;

			case 'truckManufacturer':
				this.selectedtruckManufacturer = event.value;
				break;

			case 'chartType':
				this.selectedChartType = event.value;
				this.currentPage = 0;
				return this.getRelativeTruckUtilization();

			case 'chartCriteria':
				this.selectedChartCriteria = event.value;
				break;

			default:
				return;
		}

		this.currentPage = 0;
		this.prepareUtilizationData();
	}

	changeDate(field, value) {
		this[field] = value;
		if (this.selectedPeriod === 'custom')
			this.getRelativeTruckUtilization();
	}

	prepareTruckTypes() {
		this.truckTypes = [{value: 'all', label: 'All'}];
		this.truckManufacturer = [{value: 'all', label: 'All'}];

		const truckTypesSet = new Set();
		const truckManufacturerSet = new Set();
		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (device.truck_type && !truckTypesSet.has(device.truck_type)) {
				truckTypesSet.add(device.truck_type);
				this.truckTypes.push({
					value: device.truck_type,
					label: device.truck_type,
				});
			}

			if (device.truck_manufacturer && !truckManufacturerSet.has(device.truck_manufacturer)) {
				truckManufacturerSet.add(device.truck_manufacturer);
				this.truckManufacturer.push({
					value: device.truck_manufacturer,
					label: device.truck_manufacturer,
				});
			}
		}
	}

	getFilteredDevices() {
		if (this.selectedTruckType === 'all' && this.selectedtruckManufacturer === 'all' && this.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			this.devicesCount = Object.keys(this.devices).length;
			return this.devices;
		}

		const filteredDevices: any = {};
		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (this.selectedTruckType !== 'all' && device.truck_type !== this.selectedTruckType)
				continue;

			if (this.selectedtruckManufacturer !== 'all' && device.truck_manufacturer !== this.selectedtruckManufacturer)
				continue;

			if (!this.siteDashboardService.isVintageWithinFilter(device, this.selectedBatteryVintage))
				continue;

			filteredDevices[macAddress] = device;
		}

		this.devicesCount = Object.keys(filteredDevices).length;
		return filteredDevices;
	}

	applyPagination() {
		const startIndex = this.currentPage * this.pageSize;
		const endIndex = startIndex + this.pageSize;
		this.chart.data = this.chart.data.slice(startIndex, endIndex);
	}

	nextPage() {
		if ((this.currentPage + 1) * this.pageSize < this.devicesCount) {
			this.currentPage++;
			this.prepareUtilizationData();
		}
	}

	isLastPage() {
		return (this.currentPage + 1) * this.pageSize >= this.devicesCount;
	}

	prevPage() {
		if (this.currentPage > 0) {
			this.currentPage--;
			this.prepareUtilizationData();
		}
	}

	exportCsv() {
		const csvFileHeader = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
		});

		const columnNames = this.chart.columnNames;
		const csvContent = this.chart.data;

		const data = [
			...csvFileHeader,
			columnNames,
			...csvContent
		]

		new AngularCsv(data, this.translateService.instant('utilization.relative_truck_utilization'));
	}

	updateVAxisMax(chartData: any[]) {
		if (this.selectedChartCriteria === 'daily_available_ah') {
			const maxValue = Math.max(...chartData.map(row => {
				const values = row.slice(1);
				return values.reduce((sum: number, v: number) => sum + v, 0);
			}));
			this.chart.config.vAxis['viewWindow'] = { max: Math.max(100, maxValue) };
		} else {
			delete this.chart.config.vAxis['viewWindow'];
		}
	}

	batteryVintageSelectionChanged() {
		const allValue = this.siteDashboardService.vintageOptionsDropdown[0].value;
		if (!this.selectedBatteryVintage.length)
			this.selectedBatteryVintage = [allValue];

		if (this.selectedBatteryVintage.length > 1 && this.selectedBatteryVintage.includes(allValue)) {
			if (this.selectedBatteryVintage[0] == allValue)
				this.selectedBatteryVintage = this.selectedBatteryVintage.filter(value => value !== allValue);
			else
				this.selectedBatteryVintage = [allValue];
		}
		this.currentPage = 0;
		this.prepareUtilizationData();
	}
}
